import { FlashcardsService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { FlashcardContent } from "@/components/Flashcards/FlashcardContent"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { formatDateTime } from "@/utils/format-date"
import { Badge, Box, Stack, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

type CardSearch = {
  programId?: string
}

export const Route = createFileRoute("/_layout/flashcards/notes/cards/$cardId")(
  {
    validateSearch: (search: Record<string, unknown>): CardSearch => ({
      programId:
        typeof search.programId === "string" ? search.programId : undefined,
    }),
    component: FlashcardCardNoteDetailPage,
  },
)

function FlashcardCardNoteDetailPage() {
  const { cardId } = Route.useParams()
  const { programId } = Route.useSearch()
  const breadcrumbs = useRouteBreadcrumbs("flashcards.index", [
    { label: "Student Notes", href: "/flashcards/notes" },
    { label: "Card Detail" },
  ])

  const { data, isLoading } = useQuery({
    queryKey: ["flashcards-admin-card-notes", cardId, programId],
    queryFn: () =>
      FlashcardsService.getProgramCardNotes({
        cardId,
        programId: programId as string,
      }),
    enabled: Boolean(programId),
  })

  if (!programId) {
    return (
      <PageShell title="Card Notes" breadcrumbs={breadcrumbs}>
        <AppEmptyState
          title="Program required"
          description="Open this page from the student notes list for a selected program."
        />
      </PageShell>
    )
  }

  if (isLoading) {
    return (
      <PageShell
        title="Card Notes"
        description="Students who saved or annotated this card."
        breadcrumbs={breadcrumbs}
      >
        <PageSkeleton variant="detail" rows={4} />
      </PageShell>
    )
  }

  const students = data?.data ?? []

  return (
    <PageShell
      title="Card Notes"
      description="Students who saved or annotated this card."
      breadcrumbs={breadcrumbs}
      py={{ base: 5, md: 8 }}
      contentGap={5}
    >
      {data ? (
        <Box
          borderWidth={1}
          borderColor="gray.200"
          _dark={{ borderColor: "gray.700", bg: "gray.900" }}
          borderRadius="lg"
          bg="white"
          p={{ base: 4, md: 5 }}
        >
          <Stack gap={4}>
            <Badge colorPalette="teal" w="fit-content">
              {data.deck_name}
            </Badge>
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={2} color="fg.muted">
                Front
              </Text>
              <FlashcardContent content={data.front} />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="bold" mb={2} color="fg.muted">
                Back
              </Text>
              <FlashcardContent content={data.back} />
            </Box>
          </Stack>
        </Box>
      ) : null}

      {students.length === 0 ? (
        <AppEmptyState
          title="No students saved this card"
          description="No enrolled student has saved or annotated this card in the selected program."
          variant="compact"
        />
      ) : (
        <VStack align="stretch" gap={4}>
          {students.map((row) => (
            <Box
              key={row.student_id}
              borderWidth={1}
              borderColor="gray.200"
              _dark={{ borderColor: "gray.700", bg: "gray.900" }}
              borderRadius="lg"
              bg="white"
              p={{ base: 4, md: 5 }}
            >
              <Stack gap={2}>
                <Text fontWeight="medium">
                  {row.full_name || "Unnamed student"}
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  {row.email}
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  Updated {formatDateTime(row.updated_at)}
                </Text>
                <Box
                  borderWidth={1}
                  borderColor="gray.200"
                  _dark={{ borderColor: "gray.700", bg: "gray.800" }}
                  borderRadius="md"
                  bg="gray.50"
                  p={3}
                >
                  {row.note_html ? (
                    <HtmlContent content={row.note_html} />
                  ) : (
                    <Text color="fg.muted">Bookmarked, no note.</Text>
                  )}
                </Box>
              </Stack>
            </Box>
          ))}
        </VStack>
      )}
    </PageShell>
  )
}

export default FlashcardCardNoteDetailPage
